import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGetList } from "ra-core";

/**
 * Campaign readout.
 *
 * The response signal needed no new capture: a door becomes a `companies` row
 * when it responds — that is what Promote means — so `companies.address_id` IS
 * the response. Task 7's webhooks will add delivery detail, but the thing the
 * experiment measures is already here.
 *
 * THE HOLDOUT IS THE POINT
 * ------------------------
 * 273 doors are never mailed. Their response rate is what would have happened
 * anyway — word of mouth, search, a distiller who was going to find us
 * regardless. Lift is the mailed rate minus that baseline, and it is the only
 * honest answer to "did the postcards do anything?".
 *
 * Without it you can only report "X% of mailed doors responded", which says
 * nothing about causation. That is why plan_drop() refuses HOLDOUT in SQL
 * rather than trusting a UI check.
 */

type CellRow = {
  id: string;
  cell: string;
  doors: number;
  mailable: number;
  opted_out: number;
  doors_mailed: number;
  cards: number;
  planned: number;
  sent: number;
  delivered: number;
  failed: number;
  spend_cents: number;
  responded: number;
  response_pct: number | null;
};

type ArmRow = {
  id: string;
  arm: "mailed" | "holdout";
  doors: number;
  doors_mailed: number;
  responded: number;
  response_pct: number | null;
};

const money = (cents: number) =>
  cents
    ? `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    : "—";

const pct = (v: number | null) => (v == null ? "—" : `${v}%`);

const Stat = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <Card>
    <CardContent className="pt-6">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </CardContent>
  </Card>
);

/**
 * One card per cell, for narrow screens.
 *
 * The desktop view is a nine-column table. It is wrapped in overflow-x-auto so
 * it does not break the page, but on a phone that means dragging sideways to
 * read a single row — the numbers are there and unusable at the same time.
 *
 * Stacked, only the figures that are non-zero are shown, because at this stage
 * most cells have nothing sent and a card of eight dashes communicates less
 * than a line saying so.
 */
const CellCards = ({ rows }: { rows: CellRow[] }) => (
  <div className="space-y-2">
    {rows.map((r) => {
      const quiet = r.cards === 0 && r.responded === 0;
      return (
        <div
          key={r.cell}
          className={`border rounded-md p-3 ${r.cell === "HOLDOUT" ? "bg-muted/40" : ""}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium">
              {r.cell}
              {r.cell === "HOLDOUT" && (
                <Badge variant="outline" className="ml-2">
                  baseline
                </Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground tabular-nums">
              {r.doors} doors
            </div>
          </div>
          {quiet ? (
            <div className="text-xs text-muted-foreground mt-1">
              Nothing sent yet
              {r.opted_out ? ` · ${r.opted_out} opted out` : ""}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
              {r.planned > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Planned</div>
                  <div className="tabular-nums">{r.planned}</div>
                </div>
              )}
              {r.sent > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Sent</div>
                  <div className="tabular-nums">{r.sent}</div>
                </div>
              )}
              {r.failed > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Failed</div>
                  <div className="tabular-nums text-destructive">
                    {r.failed}
                  </div>
                </div>
              )}
              {r.spend_cents > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Spend</div>
                  <div className="tabular-nums">{money(r.spend_cents)}</div>
                </div>
              )}
              {r.responded > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Responded</div>
                  <div className="tabular-nums">
                    {r.responded}
                    {r.response_pct != null && (
                      <span className="text-xs text-muted-foreground ml-1">
                        {pct(r.response_pct)}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {r.opted_out > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Opted out</div>
                  <div className="tabular-nums">{r.opted_out}</div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export function CampaignDashboard() {
  const isMobile = useIsMobile();
  const { data: cells, isPending: cellsPending } = useGetList<CellRow>(
    "campaign_by_cell",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "cell", order: "ASC" },
    },
  );
  const { data: arms, isPending: armsPending } = useGetList<ArmRow>(
    "holdout_comparison",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "arm", order: "ASC" },
    },
  );

  if (cellsPending || armsPending) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const rows = cells ?? [];
  // HOLDOUT appears in campaign_by_cell as a cell, but it is not a drop and
  // must not be averaged in with the ones that are.
  const drops = rows.filter((r) => r.cell !== "HOLDOUT");
  const holdoutCell = rows.find((r) => r.cell === "HOLDOUT");

  const totalCards = drops.reduce((s, r) => s + r.cards, 0);
  const totalSpend = drops.reduce((s, r) => s + r.spend_cents, 0);
  const totalMailed = drops.reduce((s, r) => s + r.doors_mailed, 0);
  const totalResponded = drops.reduce((s, r) => s + r.responded, 0);

  const mailedArm = arms?.find((a) => a.arm === "mailed");
  const holdArm = arms?.find((a) => a.arm === "holdout");
  const lift =
    mailedArm?.response_pct != null && holdArm?.response_pct != null
      ? Number((mailedArm.response_pct - holdArm.response_pct).toFixed(2))
      : null;

  return (
    <div className="py-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Campaign</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {drops.length} cells · {holdoutCell?.doors ?? 0} held out and never
          mailed
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label="Cards"
          value={totalCards.toLocaleString()}
          hint="excluding tests"
        />
        <Stat label="Doors mailed" value={totalMailed.toLocaleString()} />
        <Stat label="Spend" value={money(totalSpend)} />
        <Stat
          label="Responded"
          value={totalResponded.toLocaleString()}
          hint="promoted to a company"
        />
      </div>

      {/* The experiment's actual readout. Shown even at zero, because its
          absence is what makes a campaign unmeasurable and it should be
          visible from the first drop rather than bolted on afterwards. */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-medium">Mailed vs holdout</h2>
            {lift != null && (
              <Badge variant={lift > 0 ? "default" : "outline"}>
                {lift > 0 ? "+" : ""}
                {lift} pts lift
              </Badge>
            )}
          </div>
          {/* Five columns fits a phone, but only just -- wrap it so a long
              addressee or a wide locale cannot push the page sideways. */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Arm</TableHead>
                  <TableHead className="text-right">Doors</TableHead>
                  <TableHead className="text-right">Mailed</TableHead>
                  <TableHead className="text-right">Responded</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(arms ?? []).map((a) => (
                  <TableRow key={a.arm}>
                    <TableCell className="font-medium">
                      {a.arm === "holdout"
                        ? "Holdout (never mailed)"
                        : "Mailed"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {a.doors}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {a.doors_mailed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {a.responded}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {pct(a.response_pct)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            The holdout is the only thing that makes a response rate mean
            anything. Its rate is what would have happened without any postcard,
            so lift — mailed minus holdout — is the campaign's real effect.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h2 className="text-lg font-medium mb-2">By cell</h2>
          {isMobile ? (
            <CellCards rows={rows} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cell</TableHead>
                    <TableHead className="text-right">Doors</TableHead>
                    <TableHead className="text-right">Opted out</TableHead>
                    <TableHead className="text-right">Mailed</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Resp.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.cell}
                      className={
                        r.cell === "HOLDOUT" ? "bg-muted/40" : undefined
                      }
                    >
                      <TableCell className="font-medium">
                        {r.cell}
                        {r.cell === "HOLDOUT" && (
                          <Badge variant="outline" className="ml-2">
                            baseline
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.doors}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.opted_out || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.doors_mailed || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.planned || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.sent || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.failed ? (
                          <span className="text-destructive">{r.failed}</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(r.spend_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.responded || "—"}
                        {r.response_pct != null && (
                          <span className="text-muted-foreground text-xs ml-1">
                            {pct(r.response_pct)}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            Response rate is measured against doors <b>mailed</b>, not doors in
            the cell — an unmailed door cannot have responded to mail, and
            counting it would understate every cell until the whole list has
            gone out.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
